// frontend/src/pages/TicketsPage.tsx
import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import ticketService, { Ticket as ApiTicket, CreateTicketRequest, AssignTicketRequest } from '../services/ticketService';
import userService from '../services/userService';
import saleService from '../services/saleService';
import toast from 'react-hot-toast';
import {
  Scissors,
  Clock,
  User,
  Calendar,
  AlertCircle,
  Plus,
  X,
  Image,
  Search,
  Check,
  DollarSign,
  Package,
} from 'lucide-react';
import { User as SystemUser } from '../types';
import { ModelSelector } from '../components/tickets/ModelSelector';

// Tipos locales
type Priority = 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
type Status = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_WAY_TO_STORE' | 'ON_WAY_TO_CUSTOMER';
type DeliveryType = 'IN_STORE' | 'UBER';

interface CalendarTicket extends ApiTicket {
  day: number; // Día del mes para el calendario
}

const TicketsPage = () => {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tickets, setTickets] = useState<CalendarTicket[]>([]);
  const [activeTickets, setActiveTickets] = useState<ApiTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<ApiTicket | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [draggedTicket, setDraggedTicket] = useState<string | null>(null);

  // Modales
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTomarTicketModal, setShowTomarTicketModal] = useState(false);
  const [ticketATomarId, setTicketATomarId] = useState<number | null>(null);
  const [isEditingTicket, setIsEditingTicket] = useState(false);

  // Filtros
  const [filterModista, setFilterModista] = useState('');
  const [filterStatus, setFilterStatus] = useState<Status | 'ALL'>('ALL');
  const [filterPriority, setFilterPriority] = useState<Priority | 'ALL'>('ALL');
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');

  const [modistas, setModistas] = useState<SystemUser[]>([]);
  const [saleSearchFolio, setSaleSearchFolio] = useState('');
  const [searchingSale, setSearchingSale] = useState(false);
  const [foundSaleItems, setFoundSaleItems] = useState<any[]>([]);

  // Force List View on mobile/tablet (Calendar is too wide)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setViewMode('list');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Estados de formularios
  const [tomarTicketData, setTomarTicketData] = useState({
    seamstressName: '',
    deliveryType: '' as DeliveryType | '',
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    customerName: '',
    customerPhone: '',
    modelCode: '',
    adjustments: '',
    priority: 'MEDIUM' as Priority,
    photos: [] as string[],
    cost: '',
    assignedToId: '' as string | number,
    productId: '' as string,
    inventoryType: '',
    saleId: '' as string | number,
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTickets();
    loadActiveTickets();
    loadModistas();
  }, [currentMonth]);

  const loadModistas = async () => {
    if (user?.rol_usr === UserRole.ADMIN) {
      try {
        const users = await userService.getAllUsers({ role: UserRole.MODISTA, status: 'true' });
        setModistas(users);
      } catch (error) {
        console.error('Error al cargar modistas:', error);
      }
    }
  };

  const loadTickets = async () => {
    try {
      // Si es modista, solo cargar sus tickets asignados
      const params: any = { limit: 100 };

      // Cargar todos los tickets y filtrar en el frontend para permitir ver los PENDING
      // Si es modista, quiere ver sus asignados Y los disponibles

      // if (user?.rol_usr === UserRole.MODISTA && user?.id_usuario) {
      //   params.assignedToId = parseInt(user.id_usuario as string);
      // }

      const response = await ticketService.getTickets(params);
      // Convertir tickets a CalendarTickets con día del mes
      // Usar dueDate (fecha de entrega) para posicionar en el calendario
      // Filtrar solo los tickets del mes actual
      const calendarTickets = response.data
        .map(ticket => {
          const date = ticket.dueDate ? new Date(ticket.dueDate) :
            ticket.issueDate ? new Date(ticket.issueDate) :
              new Date(ticket.createdAt);
          return {
            ...ticket,
            day: date.getDate(),
            month: date.getMonth(),
            year: date.getFullYear(),
          };
        })
        .filter(ticket =>
          ticket.month === currentMonth.getMonth() &&
          ticket.year === currentMonth.getFullYear()
        )
        // Filtrado adicional para modistas: Asignados a mí O Pendientes
        .filter(ticket => {
          if (user?.rol_usr === UserRole.MODISTA && user?.id_usuario) {
            return ticket.assignedToId === parseInt(user.id_usuario as string) || ticket.status === 'PENDING';
          }
          return true;
        });
      setTickets(calendarTickets);
    } catch (error) {
      console.error('Error al cargar tickets:', error);
    }
  };

  const loadActiveTickets = async () => {
    try {
      // Si es modista, solo cargar tickets disponibles (PENDING) o asignados a ella
      if (user?.rol_usr === UserRole.MODISTA && user?.id_usuario) {
        const response = await ticketService.getTickets({
          limit: 100,
        });

        // Filtrar: PENDING (disponibles) o asignados al usuario actual
        const filteredTickets = response.data.filter(ticket =>
          ticket.status === 'PENDING' ||
          (ticket.assignedToId && ticket.assignedToId === parseInt(user.id_usuario as string))
        );

        setActiveTickets(filteredTickets);
      } else {
        // Admin y vendedoras ven todos los tickets activos
        const response = await ticketService.getActiveTickets();
        setActiveTickets(response.data);
      }
    } catch (error) {
      console.error('Error al cargar tickets activos:', error);
    }
  };

  // Función para calcular prioridad automática basada en fecha
  // Función para calcular prioridad automática basada en fecha
  const calcularPrioridad = (dueDate: string): Priority => {
    const hoy = new Date();
    const entrega = new Date(dueDate);

    // Normalizar fechas a inicio del día para comparación correcta
    hoy.setHours(0, 0, 0, 0);
    const entregaCopy = new Date(entrega);
    entregaCopy.setHours(0, 0, 0, 0);

    // Lógica para detectar si es la misma semana (Domingo a Sábado)
    const startOfWeek = new Date(hoy);
    startOfWeek.setDate(hoy.getDate() - hoy.getDay()); // Domingo de esta semana
    const endOfWeek = new Date(hoy);
    endOfWeek.setDate(hoy.getDate() + (6 - hoy.getDay())); // Sábado de esta semana

    // Si la fecha de entrega está dentro de esta semana -> URGENT
    if (entregaCopy >= startOfWeek && entregaCopy <= endOfWeek) {
      return 'URGENT';
    }

    const diferenciaDias = Math.ceil((entrega.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));

    if (diferenciaDias <= 2) return 'URGENT';
    if (diferenciaDias <= 4) return 'HIGH';
    if (diferenciaDias <= 7) return 'MEDIUM';
    return 'LOW';
  };

  // Generar días del mes para el calendario
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const handleAddTicket = (day: number) => {
    setSelectedDay(day);
    // Calcular fecha seleccionada
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    // Ajustar zona horaria local para que el string sea correcto
    const offset = selectedDate.getTimezoneOffset() * 60000;
    const localDateStr = new Date(selectedDate.getTime() - offset).toISOString().split('T')[0];
    const todayStr = new Date().toISOString().split('T')[0];

    // Calcular prioridad sugerida
    const suggestedPriority = calcularPrioridad(localDateStr);

    setFormData({
      ...formData,
      title: 'Ajuste de prenda',
      dueDate: localDateStr, // La fecha seleccionada es la fecha de entrega
      issueDate: todayStr,   // La fecha de emisión es HOY
      priority: suggestedPriority
    });

    setShowModal(true);
  };

  const handleSubmitTicket = async () => {
    if (!formData.modelCode || !formData.dueDate || !formData.customerName) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setLoading(true);
    try {
      const ticketData: CreateTicketRequest = {
        title: formData.title,
        description: formData.adjustments || 'Sin especificar',
        customerName: formData.customerName,
        customerPhone: formData.customerPhone || undefined,
        priority: formData.dueDate ? calcularPrioridad(formData.dueDate) : formData.priority,
        dueDate: formData.dueDate,
        issueDate: formData.issueDate,
        modelCode: formData.modelCode,
        adjustments: formData.adjustments,
        photos: formData.photos,
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        assignedToId: formData.assignedToId ? parseInt(formData.assignedToId as string) : undefined,
        productId: formData.productId || undefined,
        inventoryType: formData.inventoryType || undefined,
        saleId: formData.saleId ? parseInt(formData.saleId as string) : undefined,
      };

      await ticketService.createTicket(ticketData);
      toast.success('Ticket creado exitosamente');
      setShowModal(false);
      resetFormData();
      loadTickets();
      loadActiveTickets();
    } catch (error: any) {
      console.error('Error al crear ticket:', error);
      toast.error(error.response?.data?.message || 'Error al crear el ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleTomarTicket = async (ticketId: number) => {
    // Si es modista, asignar automáticamente sin abrir modal
    if (user?.rol_usr === UserRole.MODISTA) {
      setLoading(true);
      try {
        const assignData: AssignTicketRequest = {
          assignedToId: user?.id_usuario ? parseInt(user.id_usuario as string) : undefined,
          seamstressName: user?.nombre_completo || user?.username || user?.email || 'Modista',
          deliveryType: 'IN_STORE', // Valor por defecto, se puede cambiar después
        };

        await ticketService.assignTicket(ticketId, assignData);
        toast.success('Ticket asignado exitosamente');
        loadTickets();
        loadActiveTickets();
      } catch (error: any) {
        console.error('Error al asignar ticket:', error);
        toast.error(error.response?.data?.message || 'Error al asignar el ticket');
      } finally {
        setLoading(false);
      }
    } else {
      // Para admin/vendedora, abrir modal para asignar
      setTicketATomarId(ticketId);
      setShowTomarTicketModal(true);
    }
  };

  const handleConfirmarTomarTicket = async () => {
    if (!ticketATomarId || !tomarTicketData.seamstressName || !tomarTicketData.deliveryType) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const assignData: AssignTicketRequest = {
        seamstressName: tomarTicketData.seamstressName,
        deliveryType: tomarTicketData.deliveryType,
      };

      await ticketService.assignTicket(ticketATomarId, assignData);
      toast.success('Ticket asignado exitosamente');
      setShowTomarTicketModal(false);
      setTomarTicketData({ seamstressName: '', deliveryType: '' });
      setTicketATomarId(null);
      loadTickets();
      loadActiveTickets();
    } catch (error: any) {
      console.error('Error al asignar ticket:', error);
      toast.error(error.response?.data?.message || 'Error al asignar el ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (ticketId: number, newStatus: Status) => {
    setLoading(true);
    try {
      await ticketService.updateTicketStatus(ticketId, newStatus);
      toast.success('Estado actualizado');
      setShowDetailModal(false); // Cerrar el modal después de actualizar
      loadTickets();
      loadActiveTickets();
    } catch (error: any) {
      console.error('Error al actualizar estado:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el estado');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (ticket: ApiTicket) => {
    setSelectedTicket(ticket);
    setFormData({
      title: ticket.title,
      description: ticket.description || '',
      modelCode: ticket.modelCode || '',
      adjustments: ticket.adjustments || '',
      issueDate: ticket.issueDate ? new Date(ticket.issueDate).toISOString().split('T')[0] : '',
      dueDate: ticket.dueDate ? new Date(ticket.dueDate).toISOString().split('T')[0] : '',
      customerName: ticket.customerName,
      customerPhone: ticket.customerPhone || '',
      priority: ticket.priority,
      photos: ticket.photos || [],
      cost: ticket.cost?.toString() || '',
      assignedToId: ticket.assignedToId || '',
      productId: ticket.productId || '',
      inventoryType: ticket.inventoryType || '',
      saleId: ticket.saleId || '',
    });
    setIsEditingTicket(false);
    setShowDetailModal(true);
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;

    setLoading(true);
    try {
      await ticketService.updateTicket(selectedTicket.id, {
        title: formData.title,
        description: formData.adjustments,
        priority: formData.dueDate ? calcularPrioridad(formData.dueDate) : formData.priority,
        dueDate: formData.dueDate || undefined,
        modelCode: formData.modelCode,
        adjustments: formData.adjustments,
        photos: formData.photos,
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        assignedToId: formData.assignedToId ? parseInt(formData.assignedToId as string) : undefined,
        productId: formData.productId || undefined,
        inventoryType: formData.inventoryType || undefined,
      });

      toast.success('Ticket actualizado exitosamente');
      setShowDetailModal(false);
      setIsEditingTicket(false);
      loadTickets();
      loadActiveTickets();
    } catch (error: any) {
      console.error('Error al actualizar ticket:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePriority = async (ticketId: number, newPriority: Priority) => {
    setLoading(true);
    try {
      await ticketService.updateTicket(ticketId, {
        priority: newPriority,
      });

      toast.success('Prioridad actualizada exitosamente');

      // Actualizar el ticket seleccionado si está abierto el modal
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, priority: newPriority });
      }

      loadTickets();
      loadActiveTickets();
    } catch (error: any) {
      console.error('Error al cambiar prioridad:', error);
      toast.error(error.response?.data?.message || 'Error al cambiar la prioridad');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTicket = async (ticketId: number) => {
    if (!window.confirm('¿Estás seguro de eliminar este ticket?')) return;

    setLoading(true);
    try {
      await ticketService.deleteTicket(ticketId);
      toast.success('Ticket eliminado');
      setShowDetailModal(false);
      loadTickets();
      loadActiveTickets();
    } catch (error: any) {
      console.error('Error al eliminar ticket:', error);
      toast.error(error.response?.data?.message || 'Error al eliminar el ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyWhatsApp = async (ticketId: number, isOnlineStore: boolean = false) => {
    setLoading(true);
    try {
      const response = await ticketService.notifyWhatsApp(ticketId, isOnlineStore);
      toast.success('Abriendo WhatsApp...');
      // Abrir WhatsApp en una nueva ventana
      window.open(response.whatsappUrl, '_blank');
      // Recargar tickets para actualizar el estado
      loadTickets();
      loadActiveTickets();
      setShowDetailModal(false);
    } catch (error: any) {
      console.error('Error al notificar por WhatsApp:', error);
      toast.error(error.response?.data?.message || 'Error al generar notificación');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSale = async () => {
    if (!saleSearchFolio) return;
    setSearchingSale(true);
    try {
      // Buscar venta por folio
      const response = await saleService.getSales({ q: saleSearchFolio, limit: 1 });
      if (response.data && response.data.length > 0) {
        const sale = response.data[0];
        // Obtener detalles completos para los items
        const fullSale = await saleService.getSaleById(sale.id);
        const items = (fullSale as any).sale_items || [];
        setFoundSaleItems(items);
        toast.success(`Venta de ${sale.customerName} encontrada`);

        // Pre-poblar nombre del cliente si está vacío
        if (!formData.customerName) {
          setFormData(prev => ({
            ...prev,
            customerName: sale.customerName,
            customerPhone: sale.customerPhone || '',
            saleId: sale.id
          }));
        } else {
          setFormData(prev => ({ ...prev, saleId: sale.id }));
        }
      } else {
        toast.error('Folio de venta no encontrado');
        setFoundSaleItems([]);
      }
    } catch (error) {
      console.error('Error al buscar venta:', error);
      toast.error('Error al buscar la venta');
    } finally {
      setSearchingSale(false);
    }
  };

  const handleSelectItem = (item: any) => {
    // Autocompletar con detalles de la prenda
    const itemDetails = `Marca: ${item.productName || ''}, Código: ${item.productCode || ''}`;
    setFormData(prev => ({
      ...prev,
      modelCode: item.productCode || '',
      adjustments: `[${itemDetails}] - `,
      productId: item.productId.toString(),
      inventoryType: item.inventoryType || 'maestro'
    }));
    setFoundSaleItems([]);
    setSaleSearchFolio('');
    toast.success('Prenda vinculada correctamente');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileUrls = Array.from(files).map(file => URL.createObjectURL(file));
      setFormData({ ...formData, photos: [...formData.photos, ...fileUrls] });
    }
  };

  const handleDragStart = (ticketId: number) => {
    setDraggedTicket(ticketId.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (day: number) => {
    if (draggedTicket) {
      // Actualizar el día del ticket en el estado local
      setTickets(tickets.map(t =>
        t.id.toString() === draggedTicket ? { ...t, day } : t
      ));
      setDraggedTicket(null);
      // Aquí podrías también actualizar en el backend si fuera necesario
    }
  };

  const getTicketsForDay = (day: number) => {
    return tickets.filter(t => t.day === day &&
      (filterStatus === 'ALL' || t.status === filterStatus) &&
      (filterPriority === 'ALL' || t.priority === filterPriority) &&
      (!filterModista || t.seamstressName?.toLowerCase().includes(filterModista.toLowerCase()))
    );
  };

  const getFilteredTickets = () => {
    const filtered = activeTickets.filter(ticket => {
      // Filtros existentes
      const matchesMonth = new Date(ticket.dueDate || ticket.createdAt).getMonth() === currentMonth.getMonth() &&
        new Date(ticket.dueDate || ticket.createdAt).getFullYear() === currentMonth.getFullYear();

      const matchesPriority = filterPriority === 'ALL' || ticket.priority === filterPriority;

      // Filtro por Rol: Modista solo ve sus asignados o PENDING
      if (user?.rol_usr === UserRole.MODISTA) {
        const isMyTicket = ticket.assignedToId === parseInt(user.id_usuario as string);
        const isPending = ticket.status === 'PENDING';
        if (!isMyTicket && !isPending) return false;
      }

      return matchesMonth && matchesPriority;
    });

    return filtered;
  };

  // Obtener tickets filtrados para la vista de lista (con secciones para modistas)
  const getListTickets = () => {
    const baseTickets = getFilteredTickets();

    if (user?.rol_usr === UserRole.MODISTA) {
      const myTickets = baseTickets.filter(t => t.assignedToId === parseInt(user.id_usuario as string));
      const availableTickets = baseTickets.filter(t => t.status === 'PENDING');
      return { myTickets, availableTickets };
    }

    return { allTickets: baseTickets };
  };

  const resetFormData = () => {
    setFormData({
      title: '',
      description: '',
      customerName: '',
      customerPhone: '',
      modelCode: '',
      adjustments: '',
      priority: 'MEDIUM',
      photos: [],
      cost: '',
      assignedToId: '',
      productId: '',
      inventoryType: '',
      saleId: '',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: '',
    });
    setSaleSearchFolio('');
    setFoundSaleItems([]);
  };

  // Estadísticas
  const stats = {
    total: tickets.length,
    pending: tickets.filter(t => t.status === 'PENDING').length,
    inProgress: tickets.filter(t => t.status === 'IN_PROGRESS').length,
    completed: tickets.filter(t => t.status === 'COMPLETED').length,
    cancelled: tickets.filter(t => t.status === 'CANCELLED').length,
    urgent: tickets.filter(t => t.priority === 'URGENT').length,
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-800 border-red-300';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-300';
    }
  };

  const getPriorityBackground = (priority: Priority) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-50 border-red-300 hover:border-red-400';
      case 'HIGH': return 'bg-orange-50 border-orange-300 hover:border-orange-400';
      case 'MEDIUM': return 'bg-yellow-50 border-yellow-300 hover:border-yellow-400';
      case 'LOW': return 'bg-green-50 border-green-300 hover:border-green-400';
    }
  };

  const getStatusColor = (status: Status) => {
    switch (status) {
      case 'PENDING': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-orange-100 text-orange-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800';
      case 'ON_WAY_TO_STORE': return 'bg-purple-100 text-purple-800';
      case 'ON_WAY_TO_CUSTOMER': return 'bg-teal-100 text-teal-800';
    }
  };

  const getPriorityLabel = (priority: Priority) => {
    switch (priority) {
      case 'URGENT': return 'Urgente';
      case 'HIGH': return 'Alta';
      case 'MEDIUM': return 'Media';
      case 'LOW': return 'Baja';
    }
  };

  const getStatusLabel = (status: Status) => {
    switch (status) {
      case 'PENDING': return 'Disponible';
      case 'IN_PROGRESS': return 'En Proceso';
      case 'COMPLETED': return 'Completado';
      case 'CANCELLED': return 'Cancelado';
      case 'ON_WAY_TO_STORE': return 'En camino a sucursal';
      case 'ON_WAY_TO_CUSTOMER': return 'Recibido en Sucursal';
    }
  };

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <Layout>
      <div className="flex flex-col lg:flex-row h-full lg:h-full bg-gray-50 overflow-hidden">
        {/* Sidebar de tickets activos */}
        <div className="w-full lg:w-80 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 lg:overflow-y-auto flex-shrink-0 max-h-64 lg:max-h-none overflow-y-auto">
          <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Scissors className="w-5 h-5 text-blue-600" />
              Tickets Activos
            </h2>
            <p className="text-sm text-gray-600 mt-1">{activeTickets.length} en proceso</p>
          </div>

          <div className="p-4 space-y-3">
            {activeTickets.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Scissors size={48} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay tickets activos</p>
              </div>
            ) : (
              activeTickets.map(ticket => (
                <div
                  key={ticket.id}
                  onClick={() => handleOpenDetail(ticket as CalendarTicket)}
                  className={`rounded-lg p-3 border-2 cursor-pointer hover:shadow-lg transition ${getPriorityBackground(ticket.priority)}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${getPriorityColor(ticket.priority)}`}>
                      {getPriorityLabel(ticket.priority)}
                    </span>
                    {ticket.dueDate && (
                      <span className="text-[10px] text-gray-500 font-medium">
                        {new Date(ticket.dueDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {/* Cliente y Modelo */}
                  <div className="mb-2">
                    <h3 className="font-bold text-gray-900 text-sm leading-tight mb-0.5">
                      Cliente: {ticket.customerName || 'Sin nombre'}
                    </h3>
                    <p className="text-xs text-gray-600 font-medium">
                      {ticket.modelCode || ticket.title}
                    </p>
                  </div>

                  <p className="text-xs text-gray-500 mb-2 line-clamp-2">{ticket.adjustments || ticket.description}</p>

                  <div className="flex items-center justify-between text-xs text-gray-400">
                    {ticket.seamstressName ? (
                      <span className="flex items-center gap-1 text-gray-600">
                        <User className="w-3 h-3" />
                        {ticket.seamstressName}
                      </span>
                    ) : (
                      <span>Sin asignar</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Gestión de Costuras</h1>
                <p className="text-gray-600 mt-1">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  ← Anterior
                </button>
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Siguiente →
                </button>
              </div>
            </div>

            {/* Estadísticas */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              <div className="bg-white rounded-lg shadow p-4 relative overflow-hidden">
                <Calendar className="absolute right-2 top-2 w-12 h-12 text-gray-200 opacity-50" />
                <div className="relative">
                  <p className="text-xs text-gray-600 font-medium mb-1">Total</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg shadow p-4 border border-blue-200 relative overflow-hidden">
                <Clock className="absolute right-2 top-2 w-12 h-12 text-blue-200 opacity-50" />
                <div className="relative">
                  <p className="text-xs text-blue-700 font-medium mb-1">Disponibles</p>
                  <p className="text-3xl font-bold text-blue-900">{stats.pending}</p>
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg shadow p-4 border border-orange-200 relative overflow-hidden">
                <User className="absolute right-2 top-2 w-12 h-12 text-orange-200 opacity-50" />
                <div className="relative">
                  <p className="text-xs text-orange-700 font-medium mb-1">En Proceso</p>
                  <p className="text-3xl font-bold text-orange-900">{stats.inProgress}</p>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg shadow p-4 border border-green-200 relative overflow-hidden">
                <svg className="absolute right-2 top-2 w-12 h-12 text-green-200 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="relative">
                  <p className="text-xs text-green-700 font-medium mb-1">Completados</p>
                  <p className="text-3xl font-bold text-green-900">{stats.completed}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg shadow p-4 border border-gray-200 relative overflow-hidden">
                <X className="absolute right-2 top-2 w-12 h-12 text-gray-200 opacity-50" />
                <div className="relative">
                  <p className="text-xs text-gray-700 font-medium mb-1">Cancelados</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.cancelled}</p>
                </div>
              </div>

              <div className="bg-red-50 rounded-lg shadow p-4 border border-red-200 relative overflow-hidden">
                <AlertCircle className="absolute right-2 top-2 w-12 h-12 text-red-200 opacity-50" />
                <div className="relative">
                  <p className="text-xs text-red-700 font-medium mb-1">Urgentes</p>
                  <p className="text-3xl font-bold text-red-900">{stats.urgent}</p>
                </div>
              </div>
            </div>

            {/* Filtros y vista */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex flex-wrap gap-3 items-center">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as Status | 'ALL')}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  >
                    <option value="ALL">Todos los estados</option>
                    <option value="PENDING">Disponible</option>
                    <option value="IN_PROGRESS">En Proceso</option>
                    <option value="ON_WAY_TO_STORE">En camino a sucursal</option>
                    <option value="ON_WAY_TO_CUSTOMER">En camino al cliente</option>
                    <option value="COMPLETED">Completado</option>
                    <option value="CANCELLED">Cancelado</option>
                  </select>

                  <select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value as Priority | 'ALL')}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  >
                    <option value="ALL">Todas las prioridades</option>
                    <option value="URGENT">Urgente</option>
                    <option value="HIGH">Alta</option>
                    <option value="MEDIUM">Media</option>
                    <option value="LOW">Baja</option>
                  </select>

                  <input
                    type="text"
                    placeholder="Buscar modista..."
                    value={filterModista}
                    onChange={(e) => setFilterModista(e.target.value)}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  />
                </div>

                <div className="hidden lg:flex gap-2">
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-4 py-2 rounded-lg transition font-medium ${viewMode === 'calendar'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <Calendar className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-2 rounded-lg transition font-medium ${viewMode === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Vista de Calendario o Lista */}
            {viewMode === 'calendar' ? (
              <div className="bg-white rounded-lg shadow p-2 md:p-4 flex flex-col h-full overflow-hidden">
                <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 shrink-0">
                  {dayNames.map(day => (
                    <div key={day} className="text-center font-semibold text-gray-700 text-xs md:text-sm py-1">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1 md:gap-2 overflow-y-auto custom-scrollbar content-start">
                  {getDaysInMonth().map((day, index) => (
                    <div
                      key={index}
                      onDragOver={handleDragOver}
                      onDrop={() => day && handleDrop(day)}
                      className={`min-h-[100px] md:min-h-[140px] p-1 border border-gray-200 rounded-lg flex flex-col ${day ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                        } transition relative overflow-hidden`}
                    >
                      {day && (
                        <>
                          <div className="flex justify-between items-start mb-1 shrink-0">
                            <span className="text-sm md:text-lg font-semibold text-gray-700 ml-1">{day}</span>
                            <button
                              onClick={() => handleAddTicket(day)}
                              className="p-0.5 md:p-1 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex-shrink-0"
                            >
                              <Plus className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                          </div>

                          <div className="space-y-1 flex-1 overflow-y-auto custom-scrollbar pr-0.5">
                            {getTicketsForDay(day).map(ticket => (
                              <div
                                key={ticket.id}
                                draggable
                                onDragStart={() => handleDragStart(ticket.id)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDetail(ticket);
                                }}
                                className={`group p-1 md:p-1.5 rounded border cursor-pointer hover:shadow-sm transition ${getPriorityColor(ticket.priority)}`}
                              >
                                <div className="font-semibold truncate text-[10px] md:text-xs leading-tight">
                                  {ticket.modelCode || ticket.title}
                                </div>

                                <div className="hidden lg:block">
                                  <div className={`text-[9px] px-1 py-0.5 rounded mt-0.5 inline-block truncate max-w-full ${getStatusColor(ticket.status)}`}>
                                    {getStatusLabel(ticket.status)}
                                  </div>
                                </div>

                                {ticket.seamstressName && (
                                  <div className="mt-0.5 flex items-center gap-0.5 text-[9px] md:text-[10px] text-gray-600 truncate">
                                    <User className="w-2 h-2 flex-shrink-0" />
                                    <span className="truncate">{ticket.seamstressName.split(' ')[0]}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow min-h-[400px]">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-800">Vista de Lista</h2>
                </div>

                <div className="p-6 space-y-8">
                  {(() => {
                    const { myTickets, availableTickets, allTickets } = getListTickets();

                    const renderTicketCard = (ticket: ApiTicket) => (
                      <div
                        key={ticket.id}
                        className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition cursor-pointer bg-white"
                        onClick={() => handleOpenDetail(ticket)}
                      >
                        <div className={`flex-shrink-0 w-2 h-16 rounded-full ${ticket.priority === 'URGENT' ? 'bg-red-500' :
                          ticket.priority === 'HIGH' ? 'bg-orange-500' :
                            ticket.priority === 'MEDIUM' ? 'bg-yellow-500' : 'bg-green-500'
                          }`} />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-lg text-gray-900">{ticket.modelCode || ticket.title}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                              {getStatusLabel(ticket.status)}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                              {getPriorityLabel(ticket.priority)}
                            </span>
                          </div>

                          <p className="text-sm text-gray-600 mb-2 truncate">{ticket.adjustments || ticket.description}</p>

                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {ticket.issueDate ? new Date(ticket.issueDate).toLocaleDateString() : new Date(ticket.createdAt).toLocaleDateString()}
                            </span>
                            {ticket.dueDate && (
                              <span className="flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {new Date(ticket.dueDate).toLocaleDateString()}
                              </span>
                            )}
                            {ticket.cost && (
                              <span className="flex items-center gap-1 font-bold text-green-700">
                                <DollarSign className="w-3 h-3" />
                                ${Number(ticket.cost).toLocaleString()}
                              </span>
                            )}
                            {ticket.seamstressName && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {ticket.seamstressName}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex-shrink-0 flex gap-2">
                          {ticket.status === 'PENDING' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTomarTicket(ticket.id);
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm"
                            >
                              Tomar
                            </button>
                          )}
                        </div>
                      </div>
                    );

                    if (user?.rol_usr === UserRole.MODISTA) {
                      return (
                        <>
                          <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                              <Check className="w-5 h-5 text-green-600" />
                              Mis Tickets Asignados ({myTickets?.length || 0})
                            </h3>
                            <div className="space-y-3">
                              {myTickets && myTickets.length > 0 ? (
                                myTickets.map(renderTicketCard)
                              ) : (
                                <p className="text-sm text-gray-400 italic bg-gray-50 p-4 rounded-lg">No tienes tickets asignados por ahora.</p>
                              )}
                            </div>
                          </div>

                          <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                              <Plus className="w-5 h-5 text-blue-600" />
                              Tickets Disponibles ({availableTickets?.length || 0})
                            </h3>
                            <div className="space-y-3">
                              {availableTickets && availableTickets.length > 0 ? (
                                availableTickets.map(renderTicketCard)
                              ) : (
                                <p className="text-sm text-gray-400 italic bg-gray-50 p-4 rounded-lg">No hay tickets disponibles para tomar.</p>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    }

                    return (
                      <div className="space-y-3">
                        {allTickets?.map(renderTicketCard)}
                        {(!allTickets || allTickets.length === 0) && (
                          <p className="text-center py-8 text-gray-500">No se encontraron tickets con los filtros actuales.</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODALES - Continuación */}

      {/* Modal para agregar ticket */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Nuevo Ticket - Día {selectedDay}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Buscador de Venta por Folio */}
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">Vincular Venta (Opcional)</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={saleSearchFolio}
                      onChange={(e) => setSaleSearchFolio(e.target.value)}
                      placeholder="Folio de venta..."
                      className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSearchSale}
                    disabled={searchingSale || !saleSearchFolio}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {searchingSale ? '...' : 'Buscar'}
                  </button>
                </div>

                {/* Mostrar items encontrados */}
                {foundSaleItems.length > 0 && (
                  <div className="mt-3 space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                    <p className="text-[10px] font-bold text-gray-500 uppercase">Selecciona la prenda:</p>
                    {foundSaleItems.map((item, idx) => (
                      <button
                        type="button"
                        key={idx}
                        onClick={() => handleSelectItem(item)}
                        className="w-full flex items-center justify-between p-2 bg-white border border-blue-200 rounded-md hover:border-blue-400 text-left transition"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-gray-900 truncate">{item.productName}</p>
                          <p className="text-[10px] text-gray-500 truncate">SKU: {item.productCode}</p>
                        </div>
                        <Plus className="w-3 h-3 text-blue-600" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Nombre del Cliente *</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  placeholder="Nombre completo"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Teléfono</label>
                <input
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  placeholder="123-456-7890 o +[código]..."
                />
              </div>

              <div>
                <ModelSelector
                  value={formData.modelCode}
                  onChange={(value) => setFormData({ ...formData, modelCode: value })}
                />
              </div>

              {/* Selector de Modista (Solo Admin) */}
              {user?.rol_usr === UserRole.ADMIN && (
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Asignar Modista</label>
                  <select
                    value={formData.assignedToId}
                    onChange={(e) => setFormData({ ...formData, assignedToId: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  >
                    <option value="">-- Sin asignar --</option>
                    {modistas.map(m => (
                      <option key={m.id_usuario} value={m.id_usuario}>
                        {m.nombre_completo || m.username}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Ajustes a Realizar</label>
                <textarea
                  value={formData.adjustments}
                  onChange={(e) => setFormData({ ...formData, adjustments: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  rows={3}
                  placeholder="Describe los ajustes necesarios..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Fecha de Emisión</label>
                  <input
                    type="date"
                    value={formData.issueDate}
                    onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Costo del Servicio ($)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                      className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Fecha de Entrega *</label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Fotos del Vestido</label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors shadow-md font-medium">
                    <Image className="w-5 h-5" />
                    <span>Subir Fotos</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                  {formData.photos.length > 0 && (
                    <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                      {formData.photos.length} foto(s)
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleSubmitTicket}
                  disabled={loading}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50"
                >
                  {loading ? 'Creando...' : 'Crear Ticket'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para tomar ticket */}
      {showTomarTicketModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => {
          setShowTomarTicketModal(false);
          setTomarTicketData({ seamstressName: '', deliveryType: '' });
          setTicketATomarId(null);
        }}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Tomar Ticket</h3>
              <button
                onClick={() => {
                  setShowTomarTicketModal(false);
                  setTomarTicketData({ seamstressName: '', deliveryType: '' });
                  setTicketATomarId(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre de la Modista *
                </label>
                <input
                  type="text"
                  value={tomarTicketData.seamstressName}
                  onChange={(e) => setTomarTicketData({ ...tomarTicketData, seamstressName: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  placeholder="Ej: María González"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Entrega *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setTomarTicketData({ ...tomarTicketData, deliveryType: 'IN_STORE' })}
                    className={`p-4 border-2 rounded-lg transition font-medium ${tomarTicketData.deliveryType === 'IN_STORE'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="text-sm">En tienda</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTomarTicketData({ ...tomarTicketData, deliveryType: 'UBER' })}
                    className={`p-4 border-2 rounded-lg transition font-medium ${tomarTicketData.deliveryType === 'UBER'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="text-sm">Por Uber</span>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleConfirmarTomarTicket}
                  disabled={loading}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50"
                >
                  {loading ? 'Asignando...' : 'Confirmar'}
                </button>
                <button
                  onClick={() => {
                    setShowTomarTicketModal(false);
                    setTomarTicketData({ seamstressName: '', deliveryType: '' });
                    setTicketATomarId(null);
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalles del ticket */}
      {showDetailModal && selectedTicket && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => {
          setShowDetailModal(false);
          setIsEditingTicket(false);
        }}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h3 className="text-2xl font-bold text-gray-900">{selectedTicket.modelCode || selectedTicket.title}</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(selectedTicket.status)}`}>
                  {getStatusLabel(selectedTicket.status)}
                </span>
                <span className={`px-3 py-1 rounded text-sm font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                  Prioridad {getPriorityLabel(selectedTicket.priority)}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setIsEditingTicket(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Contenido */}
            <div className="p-6 space-y-6">
              {!isEditingTicket ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-600">Cliente</label>
                        <p className="text-lg text-gray-900 mt-1">{selectedTicket.customerName}</p>
                        {selectedTicket.customerPhone && (
                          <p className="text-sm text-gray-600">{selectedTicket.customerPhone}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-gray-600">Código de Modelo</label>
                        <p className="text-lg text-gray-900 mt-1">{selectedTicket.modelCode || 'Sin especificar'}</p>
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-gray-600">Ajustes a Realizar</label>
                        <p className="text-gray-900 mt-1">{selectedTicket.adjustments || selectedTicket.description}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Fecha de Emisión
                          </label>
                          <p className="text-gray-900 mt-1">
                            {selectedTicket.issueDate
                              ? new Date(selectedTicket.issueDate).toLocaleDateString()
                              : new Date(selectedTicket.createdAt).toLocaleDateString()}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            Fecha de Entrega
                          </label>
                          <p className="text-gray-900 mt-1">
                            {selectedTicket.dueDate ? new Date(selectedTicket.dueDate).toLocaleDateString() : 'Sin especificar'}
                          </p>
                        </div>
                      </div>

                      {selectedTicket.seamstressName && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                            <User className="w-4 h-4" />
                            Modista Asignada
                          </label>
                          <p className="text-gray-900 mt-1">{selectedTicket.seamstressName}</p>
                        </div>
                      )}

                      {selectedTicket.cost !== undefined && selectedTicket.cost !== null && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            Costo del Servicio
                          </label>
                          <p className="text-lg font-bold text-green-700 mt-1">
                            ${Number(selectedTicket.cost).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      )}

                      {selectedTicket.productId && (
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                          <label className="text-xs font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            Prenda Vinculada
                          </label>
                          <p className="text-sm text-gray-900 mt-1 font-medium">{selectedTicket.modelCode}</p>
                          <p className="text-[10px] text-gray-500 uppercase">ID: {selectedTicket.productId.toString()} | {selectedTicket.inventoryType}</p>
                        </div>
                      )}

                      {selectedTicket.deliveryType && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                            Tipo de Entrega
                          </label>
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedTicket.deliveryType === 'IN_STORE'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                              }`}>
                              {selectedTicket.deliveryType === 'IN_STORE' ? 'En tienda' : 'Por Uber'}
                            </span>
                          </div>
                        </div>
                      )}

                      {selectedTicket.deliveryAddress && selectedTicket.deliveryType === 'UBER' && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Dirección de Entrega
                          </label>
                          <p className="text-gray-900 mt-1 bg-yellow-50 p-2 rounded border border-yellow-200">
                            {selectedTicket.deliveryAddress}
                          </p>
                        </div>
                      )}

                      {/* Selector de Prioridad (solo para Admin y Vendedoras) */}
                      {(user?.rol_usr === UserRole.ADMIN || user?.rol_usr === UserRole.VENDEDORA || user?.rol_usr === UserRole.VENDEDORA_EN_LINEA) && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            Cambiar Prioridad
                          </label>
                          <select
                            value={selectedTicket.priority}
                            onChange={(e) => {
                              handleChangePriority(selectedTicket.id, e.target.value as Priority);
                            }}
                            className="w-full mt-1 px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                          >
                            <option value="LOW">Baja</option>
                            <option value="MEDIUM">Media</option>
                            <option value="HIGH">Alta</option>
                            <option value="URGENT">Urgente</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Solo administradores y vendedoras pueden cambiar la prioridad
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="text-sm font-semibold text-gray-600">Estado Actual</label>
                        <select
                          value={selectedTicket.status}
                          onChange={(e) => {
                            handleChangeStatus(selectedTicket.id, e.target.value as Status);
                          }}
                          className="w-full mt-1 px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                        >
                          <option value="PENDING">Disponible</option>
                          <option value="IN_PROGRESS">En Proceso</option>
                          {selectedTicket.deliveryType === 'UBER' && (
                            <option value="ON_WAY_TO_CUSTOMER">Terminado - En camino al cliente (Uber)</option>
                          )}
                          {selectedTicket.deliveryType === 'IN_STORE' && (
                            <option value="ON_WAY_TO_STORE">Terminado - En camino a sucursal</option>
                          )}
                          <option value="COMPLETED">Completado y Entregado al Cliente</option>
                          <option value="CANCELLED">Cancelado</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Solo marca como "Completado" cuando el cliente ya recogió la prenda
                        </p>
                      </div>
                    </div>

                    {/* Fotos */}
                    <div>
                      <label className="text-sm font-semibold text-gray-600 flex items-center gap-1 mb-3">
                        <Image className="w-4 h-4" />
                        Fotos del Vestido ({selectedTicket.photos?.length || 0})
                      </label>
                      {selectedTicket.photos && selectedTicket.photos.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3">
                          {selectedTicket.photos.map((foto, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={foto}
                                alt={`Foto ${index + 1}`}
                                className="w-full h-40 object-cover rounded-lg border border-gray-200"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition rounded-lg flex items-center justify-center">
                                <button
                                  onClick={() => window.open(foto, '_blank')}
                                  className="opacity-0 group-hover:opacity-100 transition bg-white text-gray-900 px-3 py-1 rounded-lg text-sm font-medium"
                                >
                                  Ver completa
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-400">
                          <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No hay fotos adjuntas</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    {/* FLUJO 1: Ticket Disponible -> Tomar */}
                    {selectedTicket.status === 'PENDING' && (
                      <button
                        onClick={() => {
                          handleTomarTicket(selectedTicket.id);
                          setShowDetailModal(false);
                        }}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold"
                      >
                        Tomar Ticket
                      </button>
                    )}

                    {/* FLUJO 2: Modista termina el ajuste -> En camino a sucursal */}
                    {selectedTicket.status === 'IN_PROGRESS' &&
                      user?.rol_usr === UserRole.MODISTA &&
                      selectedTicket.assignedToId === parseInt(user?.id_usuario as string) && (
                        <button
                          onClick={() => handleChangeStatus(selectedTicket.id, 'ON_WAY_TO_STORE')}
                          disabled={loading}
                          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold disabled:opacity-50"
                        >
                          Terminar y Enviar a Sucursal
                        </button>
                      )}

                    {/* FLUJO 3: Vendedora recibe en sucursal -> Recibido (ON_WAY_TO_CUSTOMER) */}
                    {selectedTicket.status === 'ON_WAY_TO_STORE' &&
                      (user?.rol_usr === UserRole.VENDEDORA || user?.rol_usr === UserRole.VENDEDORA_EN_LINEA || user?.rol_usr === UserRole.ADMIN) && (
                        <button
                          onClick={() => handleChangeStatus(selectedTicket.id, 'ON_WAY_TO_CUSTOMER')}
                          disabled={loading}
                          className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition font-semibold disabled:opacity-50 flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Confirmar Recepción en Sucursal
                        </button>
                      )}

                    {/* FLUJO 4: Vendedora notifica al cliente y entrega */}
                    {selectedTicket.status === 'ON_WAY_TO_CUSTOMER' &&
                      (user?.rol_usr === UserRole.VENDEDORA || user?.rol_usr === UserRole.VENDEDORA_EN_LINEA || user?.rol_usr === UserRole.ADMIN) && (
                        <div className="flex gap-2">
                          {/* Botón WhatsApp */}
                          <button
                            onClick={() => handleNotifyWhatsApp(selectedTicket.id, user?.rol_usr === UserRole.VENDEDORA_EN_LINEA)}
                            disabled={loading}
                            className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-semibold disabled:opacity-50 flex items-center gap-2"
                            title="Notificar por WhatsApp"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                            Notificar
                          </button>

                          {/* Botón Entregar */}
                          <button
                            onClick={() => {
                              if (window.confirm('¿Confirmas que el cliente ha recibido la prenda físicamente?')) {
                                handleChangeStatus(selectedTicket.id, 'COMPLETED');
                              }
                            }}
                            disabled={loading}
                            className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50 flex-1"
                          >
                            Entregar al Cliente (Completar)
                          </button>
                        </div>
                      )}

                    <div className="text-xs text-gray-500 mt-2 block w-full text-center">
                      <strong>Nota:</strong> El ticket se marca como "Completado" solo cuando el cliente recoge la prenda
                    </div>
                    <button
                      onClick={() => setIsEditingTicket(true)}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                    >
                      Editar
                    </button>
                    {user?.rol_usr === UserRole.ADMIN && (
                      <button
                        onClick={() => handleDeleteTicket(selectedTicket.id)}
                        className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
                      >
                        Eliminar
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setIsEditingTicket(false);
                      }}
                      className="ml-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                    >
                      Cerrar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Modo de edición */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <ModelSelector
                          value={formData.modelCode}
                          onChange={(value) => setFormData({ ...formData, modelCode: value })}
                          disabled={user?.rol_usr === UserRole.MODISTA}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-1">Fecha de Entrega *</label>
                        <input
                          type="date"
                          value={formData.dueDate}
                          onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                          className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${user?.rol_usr === UserRole.MODISTA ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          disabled={user?.rol_usr === UserRole.MODISTA}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-1">Costo ($)</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="number"
                            step="0.01"
                            value={formData.cost}
                            onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Ajustes a Realizar</label>
                      <textarea
                        value={formData.adjustments}
                        onChange={(e) => setFormData({ ...formData, adjustments: e.target.value })}
                        className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${user?.rol_usr === UserRole.MODISTA ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        rows={4}
                        disabled={user?.rol_usr === UserRole.MODISTA}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Fotos del Vestido</label>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors shadow-md font-medium">
                            <Image className="w-5 h-5" />
                            <span>Agregar Fotos</span>
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                          </label>
                          {formData.photos.length > 0 && (
                            <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                              {formData.photos.length} foto(s)
                            </span>
                          )}
                        </div>

                        {formData.photos.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {formData.photos.map((foto, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={foto}
                                  alt={`Foto ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                />
                                <button
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      photos: formData.photos.filter((_, i) => i !== index)
                                    });
                                  }}
                                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Acciones de edición */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleUpdateTicket}
                      disabled={loading}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50"
                    >
                      {loading ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingTicket(false);
                        setFormData({
                          title: selectedTicket.title,
                          description: selectedTicket.description || '',
                          customerName: selectedTicket.customerName,
                          customerPhone: selectedTicket.customerPhone || '',
                          modelCode: selectedTicket.modelCode || '',
                          adjustments: selectedTicket.adjustments || '',
                          priority: selectedTicket.priority,
                          photos: selectedTicket.photos || [],
                          cost: selectedTicket.cost?.toString() || '',
                          assignedToId: selectedTicket.assignedToId || '',
                          productId: selectedTicket.productId || '',
                          inventoryType: selectedTicket.inventoryType || '',
                          saleId: selectedTicket.saleId || '',
                          issueDate: selectedTicket.issueDate ? new Date(selectedTicket.issueDate).toISOString().split('T')[0] : '',
                          dueDate: selectedTicket.dueDate ? new Date(selectedTicket.dueDate).toISOString().split('T')[0] : '',
                        });
                      }}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                    >
                      Cancelar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default TicketsPage;

